import { z } from 'zod';
import {
  AgenteOpencodeSchema,
  ESFORCO_SEM_VARIANTE,
  EsforcoOpencodeSchema,
  provedorOpencodeNaoTratado,
  ProvedorOpencodeSchema,
  type EsforcoOpencode,
  type ProvedorOpencode,
} from '@remoto/protocolo';

import { PapelMaquinaSchema } from '../maquinas';
import { PROVEDOR_PADRAO_OPENCODE, provedorDoModelo } from './provedores';
import type { ModeloCombinado, ModeloDaMaquina } from '../sessoes/modelos';

/**
 * A configuração do OpenCode e o catálogo do OpenRouter (F16, ADR-0022;
 * Interface congelada, item 9).
 *
 * Mora aqui, fora de `sessoes/modelos.ts`, porque aquele arquivo é disputado
 * com a economia de tokens: ele recebe só o despacho de uma linha para cá.
 */

/**
 * O id de um modelo do OpenRouter: `provedor/modelo`, sem espaço.
 *
 * A forma é a única coisa que o schema garante — se o modelo existe, aceita
 * `tools` e imagem é pergunta do catálogo. Mas a forma já basta para recusar o
 * engano que mais importa: `opus`, `sonnet` e `gpt-6-astra` não têm barra, e
 * nenhum deles chega ao OpenCode por um seletor que ficou no motor errado.
 */
export const IdModeloOpencodeSchema = z
  .string()
  .min(3)
  .max(200)
  .regex(/^[^\s/]+\/[^\s]+$/, 'id_de_modelo_invalido: o id tem a forma provedor/modelo');

/**
 * O modelo padrão das sessões OPENCODE no NanoGPT (D44 do plano de revisão de
 * out/2026): o GLM 5.3 Flash Uncensored — o mais rápido e estável dos seis do
 * dono (88 tokens/s, "Reliable"), 1 M de contexto, ferramentas e imagem,
 * incluído na assinatura. Medido no binário 1.18.32: responde e chama
 * ferramenta.
 */
export const MODELO_PADRAO_OPENCODE = 'nano-gpt/z-ai/glm-5.3-flash-uncensored';

/**
 * O padrão do OpenRouter (D13 da F16): o DeepSeek V4.1 Flash. Guardado com o
 * provedor (D11): vale quando o OpenRouter for o provedor ativo.
 */
export const MODELO_PADRAO_OPENROUTER = 'deepseek/deepseek-v4.1-flash';

/**
 * O esforço padrão. **Provisório até o dono confirmar**, e por isso uma
 * constante só: a Fase 0 mediu que o esforço do OpenCode é o `variant` de cada
 * modelo (o DeepSeek flash tem `[low, high, max]`, sem `medium`), e o `medium`
 * do plano não existiria no próprio padrão. `padrao` é "não mandar variant" —
 * o provedor decide —, gravado explicitamente.
 */
export const ESFORCO_PADRAO_OPENCODE: EsforcoOpencode = ESFORCO_SEM_VARIANTE;

/**
 * O modelo que lê as IMAGENS (D23 revista pelo dono em 2026-09-24).
 *
 * Medido: o `deepseek/deepseek-v4.1-flash` aparece no catálogo com
 * `image: true` e, pelo OpenRouter, responde "não vejo imagem nenhuma"; o
 * `z-ai/glm-5.3-flash` viu certo. O catálogo, então, não basta para decidir —
 * e a decisão do dono foi: o turno com imagem vai SOZINHO para um modelo com
 * visão, só naquele turno (`modeloDoTurnoComImagem`).
 */
export const MODELO_DE_VISAO_OPENROUTER = 'z-ai/glm-5.3-flash';

/**
 * No NanoGPT, o modelo de visão é O MESMO do padrão (D44): o GLM 5.3 Flash
 * enxerga imagem, então não há troca de modelo no meio da conversa. A troca só
 * acontece quando a sessão roda num modelo sem imagem (o GLM 4.6).
 */
export const MODELO_DE_VISAO_PADRAO_OPENCODE = MODELO_PADRAO_OPENCODE;

/**
 * Os favoritos do NanoGPT: os SEIS do dono (D15, D44), o padrão primeiro.
 * Todos com ferramentas e na assinatura; o GLM 4.6 não aceita imagem.
 *
 * **Id do NanoGPT muda sem aviso** (medido em 26/10): de manhã o Gemma Chimera
 * era `google/gemma-…` no host direto e sem prefixo no site; à tarde os dois
 * convergiram para o sem prefixo. Por isso a API cruza os favoritos com o
 * catálogo vivo antes de declará-los ao daemon — o que sumiu fica de fora e
 * aparece na tela, em vez de virar erro na conversa.
 */
export const FAVORITOS_PADRAO: readonly string[] = Object.freeze([
  MODELO_PADRAO_OPENCODE,
  'nano-gpt/z-ai/glm-5.3-flash-apex-abliterated',
  'nano-gpt/Gemma-4-31B-Gembrain-uncensored-heretic',
  'nano-gpt/GLM-4.6-Derestricted-v5',
  'nano-gpt/gemma-4-26b-a4b-it-chimeraxheretic',
  'nano-gpt/Qwen3.5-27B-Queen-Derestricted',
]);

/** Os favoritos do OpenRouter (D13 da F16), guardados com ele. */
export const FAVORITOS_OPENROUTER: readonly string[] = Object.freeze([
  MODELO_PADRAO_OPENROUTER,
  'z-ai/glm-5.3-flash',
  'moonshotai/kimi-k2.7-code',
  'deepseek/deepseek-v4-pro',
  'z-ai/glm-5.3',
]);

/** Os padrões de cada provedor: o que vale quando ele é o ativo e ninguém escolheu outra coisa. */
export function padroesDoProvedor(provedor: ProvedorOpencode): {
  padrao: string;
  visao: string;
  mestre: string;
  favoritos: readonly string[];
} {
  switch (provedor) {
    case 'nanogpt':
      return {
        padrao: MODELO_PADRAO_OPENCODE,
        visao: MODELO_DE_VISAO_PADRAO_OPENCODE,
        // D44: a mestre OpenCode no GLM 5.3 Flash, o mesmo do padrão.
        mestre: MODELO_PADRAO_OPENCODE,
        favoritos: FAVORITOS_PADRAO,
      };
    case 'openrouter':
      return {
        padrao: MODELO_PADRAO_OPENROUTER,
        visao: MODELO_DE_VISAO_OPENROUTER,
        mestre: MODELO_PADRAO_OPENROUTER,
        favoritos: FAVORITOS_OPENROUTER,
      };
    default:
      return provedorOpencodeNaoTratado(provedor);
  }
}

/** O teto de favoritos: a lista aparece inteira acima da busca, e 30 já é uma tela. */
export const MAXIMO_DE_FAVORITOS = 30;

/**
 * A configuração do OpenCode da frota: UMA linha (`configuracoes_opencode`,
 * `id='frota'`), editada pelo ADMIN na tela de Configurações (D17).
 *
 * Os padrões são os da D13 e da D16, e existem para a linha que ainda não foi
 * salva nenhuma vez: a API devolve esta forma com os padrões, e não 404.
 *
 * O modelo da MESTRE é campo próprio (D2): ela pode rodar num modelo diferente
 * das sessões de trabalho — e é daqui, e só daqui, que ele sai
 * (`modelo_da_mestre_vem_da_config` recusa o modelo mandado na criação).
 */
export const ConfiguracaoOpencodeSchema = z
  .object({
    /**
     * Quem atende os modelos (F2 da revisão de out/2026, D11): o NanoGPT por
     * padrão; o OpenRouter, guardado. Todo modelo desta configuração tem de ser
     * DESTE provedor — o outro não tem chave no ambiente do `serve`, e o modelo
     * dele não responderia (o daemon recusa com motivo, nunca trava calado).
     */
    provedor: ProvedorOpencodeSchema.default(PROVEDOR_PADRAO_OPENCODE),
    modeloPadrao: IdModeloOpencodeSchema.default(MODELO_PADRAO_OPENCODE),
    esforcoPadrao: EsforcoOpencodeSchema.default(ESFORCO_PADRAO_OPENCODE),
    favoritos: z
      .array(IdModeloOpencodeSchema)
      .max(MAXIMO_DE_FAVORITOS)
      .refine((l) => new Set(l).size === l.length, 'favorito_repetido: cada modelo aparece uma vez')
      .default(() => [...FAVORITOS_PADRAO]),
    modeloMestre: IdModeloOpencodeSchema.default(MODELO_PADRAO_OPENCODE),
    esforcoMestre: EsforcoOpencodeSchema.default(ESFORCO_PADRAO_OPENCODE),
    /** Quem lê o turno com imagem (D23 revista): ver `modeloDoTurnoComImagem`. */
    modeloDeVisao: IdModeloOpencodeSchema.default(MODELO_DE_VISAO_PADRAO_OPENCODE),
  })
  .superRefine((config, ctx) => {
    const campos = [
      ['modeloPadrao', config.modeloPadrao],
      ['modeloMestre', config.modeloMestre],
      ['modeloDeVisao', config.modeloDeVisao],
      ...config.favoritos.map((id, i) => [`favoritos.${i}`, id] as const),
    ] as const;
    for (const [campo, id] of campos) {
      if (provedorDoModelo(id) !== config.provedor) {
        ctx.addIssue({
          code: 'custom',
          path: campo.split('.').map((p) => (/^\d+$/.test(p) ? Number(p) : p)),
          message: `modelo_de_outro_provedor: ${id} nao e do provedor ${config.provedor}`,
        });
      }
    }
  });
export type ConfiguracaoOpencode = z.infer<typeof ConfiguracaoOpencodeSchema>;

/**
 * O modelo que atende UM turno que traz imagem (D23 revista).
 *
 * Se a sessão já roda no modelo de visão da config, nada muda. Se não, o turno
 * vai ao modelo de visão — SÓ aquele turno: a sessão continua no modelo dela, e
 * o turno seguinte, sem imagem, volta a ele. `trocou` existe para o host dizer
 * isso na conversa em vez de trocar calado.
 *
 * Função pura, e não uma regra dentro do host, pela razão de sempre: a regra
 * que decide para onde a imagem da pessoa vai precisa de teste próprio, e o
 * painel e a API leem a MESMA resposta.
 */
export function modeloDoTurnoComImagem(
  modeloDaSessao: string,
  config: Pick<ConfiguracaoOpencode, 'modeloDeVisao'>,
): { modelo: string; trocou: boolean } {
  if (modeloDaSessao === config.modeloDeVisao) return { modelo: modeloDaSessao, trocou: false };
  return { modelo: config.modeloDeVisao, trocou: true };
}

/**
 * D46 (dono, 26/10): a conversa OPENCODE gravada com modelo de um provedor que
 * NÃO é o ativo segue no padrão do ativo — o da mestre, se ela é a mestre.
 * `null` quando nada muda (o modelo já é do provedor ativo, ou a linha não
 * tem modelo e a reserva do gateway já é do padrão).
 *
 * O esforço volta ao da config junto: o `variant` é POR MODELO (Fase 0), e o
 * do modelo antigo pode não existir no novo.
 *
 * Função pura, e não uma regra dentro do gateway: quem decide que a conversa
 * do dono muda de modelo precisa de teste próprio.
 */
export function modeloNoProvedorAtivo(
  sessao: { funcao: 'NORMAL' | 'MESTRE'; modelo: string | null },
  config: ConfiguracaoOpencode,
): { modelo: string; esforco: string; provedorAntigo: ProvedorOpencode } | null {
  if (sessao.modelo === null) return null;
  const provedorAntigo = provedorDoModelo(sessao.modelo);
  if (provedorAntigo === config.provedor) return null;
  return sessao.funcao === 'MESTRE'
    ? { modelo: config.modeloMestre, esforco: config.esforcoMestre, provedorAntigo }
    : { modelo: config.modeloPadrao, esforco: config.esforcoPadrao, provedorAntigo };
}

/**
 * Se um turno traz imagem: algum anexo com `mime` de imagem.
 *
 * Pelo `mime` que o servidor conferiu no envio, e nunca pela extensão do nome
 * — `foto.png` com `mime` de texto não é imagem para o modelo.
 */
export function turnoTemImagem(anexos: readonly { mime: string }[]): boolean {
  return anexos.some((a) => a.mime.toLowerCase().startsWith('image/'));
}

/**
 * O esforço existe NAQUELE modelo? A regra UMA vez, para a tela de
 * Configurações, a criação de sessão e o painel perguntarem a mesma coisa.
 *
 * `padrao` (não mandar `variant`: o provedor decide) vale em qualquer modelo;
 * o resto tem de estar na lista do modelo — que é POR MODELO (Fase 0: o
 * DeepSeek V4.1 Flash tem `[low, high, max]` e nenhum `medium`; o Kimi não tem
 * nenhum). Duas cópias desta linha divergiriam no dia em que a regra mudasse,
 * e a tela aceitaria um padrão que a criação recusa.
 */
export function esforcoExisteNoModelo(
  esforco: string,
  esforcosDoModelo: readonly string[],
): boolean {
  return esforco === ESFORCO_SEM_VARIANTE || esforcosDoModelo.includes(esforco);
}

/** A chave vista pela tela: só o final, a versão e quando trocou (D5). NUNCA o valor. */
export const ChaveOpencodeVisivelSchema = z.strictObject({
  /** Os quatro últimos caracteres (`…ab12`), para o dono reconhecer qual chave é. */
  final: z.string().length(4),
  versao: z.number().int().positive(),
  trocadaEm: z.iso.datetime(),
});
export type ChaveOpencodeVisivel = z.infer<typeof ChaveOpencodeVisivelSchema>;

/** Uma máquina na tabela de estado da tela de Configurações. */
export const MaquinaNoEstadoOpencodeSchema = z.object({
  maquinaId: z.string().min(1),
  nome: z.string(),
  papel: PapelMaquinaSchema,
  online: z.boolean(),
  /** O último retrato do pulso (`maquinas.opencode`); `null` é "sem OpenCode". */
  opencode: AgenteOpencodeSchema.nullable(),
  /**
   * O daemon é anterior ao que fala o provedor ATIVO (F2: o NanoGPT pede o
   * 0.8.0). Ele recebe `chave: null` e recusa sessão nova — o honesto, porque
   * não saberia falar com o NanoGPT —, e a tela diz "daemon desatualizado" em
   * vez de "sem chave", que mandaria o dono colar a chave de novo.
   */
  desatualizada: z.boolean(),
});
export type MaquinaNoEstadoOpencode = z.infer<typeof MaquinaNoEstadoOpencodeSchema>;

/**
 * `GET /configuracoes/opencode`: a config, a chave (só o final), o estado por
 * máquina e o agregado "aplicada em X de Y" — calculado no service, nunca
 * coluna (regra 3 do `api.md`).
 *
 * `chave` é `strictObject` por dentro: a resposta que ganhasse um `valor` por
 * engano seria recusada no parse do painel, em vez de desenhada.
 */
export const EstadoDaConfigOpencodeSchema = z.object({
  config: ConfiguracaoOpencodeSchema,
  /** A chave do provedor ATIVO — a que a frota usa. */
  chave: ChaveOpencodeVisivelSchema.nullable(),
  /**
   * As duas, cada uma com o seu final (F2, D11): a do provedor guardado
   * continua guardada, e a tela mostra as duas para o dono reconhecer qual é
   * qual. `strictObject`, como a `chave`: um campo a mais é recusado no parse.
   */
  chaves: z.strictObject({
    nanogpt: ChaveOpencodeVisivelSchema.nullable(),
    openrouter: ChaveOpencodeVisivelSchema.nullable(),
  }),
  /**
   * A versão que viaja ao daemon: soma das duas chaves com a parcela da
   * configuração (`F2-desenho.md` §8 do plano de revisão). É contra ELA que o
   * "aplicada em X de Y" conta — não contra a versão da chave do provedor.
   */
  versaoDaCredencial: z.number().int().nonnegative(),
  /**
   * Os modelos da config (padrão, mestre, visão e favoritos) que o catálogo
   * VIVO do provedor não tem mais (F2: id do NanoGPT muda sem aviso, medido).
   * Ficam fora da declaração ao daemon; a tela avisa. `null` = o catálogo não
   * respondeu agora, e "não sei" não é "nenhum sumiu".
   */
  foraDoCatalogo: z.array(IdModeloOpencodeSchema).nullable(),
  maquinas: z.array(MaquinaNoEstadoOpencodeSchema),
  aplicadas: z.object({
    aplicadas: z.number().int().nonnegative(),
    elegiveis: z.number().int().nonnegative(),
  }),
});
export type EstadoDaConfigOpencode = z.infer<typeof EstadoDaConfigOpencodeSchema>;

/**
 * Corpo do `PUT /configuracoes/opencode/chave`.
 *
 * O campo se chama `segredo` DE PROPÓSITO: é um nome que a auditoria já redige
 * (`CAMPOS_REDIGIDOS`). A auditoria procura segredo pelo NOME da chave — um
 * `apiKey` ou `chave` aqui seria gravado em claro em `eventos_auditoria`, que é
 * append-only.
 */
export const TrocarChaveOpencodeSchema = z.strictObject({
  segredo: z.string().trim().min(20).max(300),
  /**
   * De qual provedor é a chave (F2). Ausente = o provedor ATIVO, que é o que a
   * tela de antes da F2 queria dizer. Trocar a chave do provedor guardado não
   * mexe na frota: o daemon só adota a versão nova, sem reiniciar.
   */
  provedor: ProvedorOpencodeSchema.optional(),
});
export type TrocarChaveOpencode = z.infer<typeof TrocarChaveOpencodeSchema>;

/**
 * Corpo do `POST /configuracoes/opencode/chave/testar`: a chave candidata, ou
 * nada para testar a que já está guardada. Mesmo nome, mesma razão.
 */
export const TestarChaveOpencodeSchema = z.strictObject({
  segredo: z.string().trim().min(20).max(300).optional(),
  /** De qual provedor (F2). Ausente = o ativo. */
  provedor: ProvedorOpencodeSchema.optional(),
});
export type TestarChaveOpencode = z.infer<typeof TestarChaveOpencodeSchema>;

/** Consulta do `DELETE /configuracoes/opencode/chave?provedor=` (F2). Ausente = o ativo. */
export const RemoverChaveOpencodeSchema = z.strictObject({
  provedor: ProvedorOpencodeSchema.optional(),
});
export type RemoverChaveOpencode = z.infer<typeof RemoverChaveOpencodeSchema>;

/**
 * A resposta do "Testar chave" (D17), lida do `GET /api/v1/key` do OpenRouter:
 * teto e uso da chave. `null` é "o OpenRouter não disse" — chave sem teto tem
 * `limiteUsd: null`, e isso é informação, não falha. No NanoGPT (F2) só o
 * `restanteUsd` vem: é o saldo da conta (`check-balance`), que não tem teto
 * por chave.
 *
 * `erro` é texto para humano, e nunca carrega a chave.
 */
export const TesteDeChaveSchema = z.object({
  ok: z.boolean(),
  limiteUsd: z.number().nullable(),
  usoUsd: z.number().nullable(),
  restanteUsd: z.number().nullable(),
  erro: z.string().max(300).nullable(),
});
export type TesteDeChave = z.infer<typeof TesteDeChaveSchema>;

/**
 * Um modelo do catálogo do OpenRouter, como a tela de criar o mostra (D12).
 *
 * `aceitaImagem` é INFORMATIVO (o que o catálogo diz) e **não decide o clipe**:
 * no OPENCODE o clipe aparece sempre, e quem garante que a imagem é lida é a
 * troca de modelo no turno (`modeloDoTurnoComImagem`, D23 revista) — o
 * catálogo disse `image: true` para um modelo que não vê a imagem.
 *
 * `esforcos` é a lista de `variant` DAQUELE modelo — pode ser vazia (o Kimi
 * não tem nenhum), e aí o seletor de esforço some e a sessão grava `padrao`.
 * É `string[]` e não um enum da casa porque os nomes são do catálogo (Fase 0).
 *
 * Preço em US$ por milhão de tokens, e `null` quando o catálogo não diz:
 * número inventado aqui viraria uma estimativa de custo que ninguém mediu.
 */
export const ModeloOpencodeSchema = z.object({
  id: IdModeloOpencodeSchema,
  nome: z.string().min(1).max(200),
  entradaUsdPorMTok: z.number().nonnegative().nullable(),
  saidaUsdPorMTok: z.number().nonnegative().nullable(),
  contexto: z.number().int().positive().nullable(),
  aceitaImagem: z.boolean(),
  esforcos: z.array(z.string().regex(/^[a-z]{2,12}$/)).max(12),
  favorito: z.boolean(),
  /**
   * Preço da leitura de cache, US$ por milhão (F2: o NanoGPT o dá, e o daemon o
   * declara ao OpenCode para o equivalente sair certo). Ausente = o catálogo não
   * disse — nunca zero.
   */
  cacheLidoUsdPorMTok: z.number().nonnegative().nullable().optional(),
  /**
   * O modelo entra na ASSINATURA do NanoGPT (`subscription.included`, D43/D45).
   * Ausente = não se sabe (o OpenRouter não tem assinatura).
   */
  incluidoNaAssinatura: z.boolean().optional(),
});
export type ModeloOpencode = z.infer<typeof ModeloOpencodeSchema>;

/**
 * A conta do NanoGPT como o cartão do Uso a mostra (D43, P3 aprovado em
 * 26/10). Três leituras independentes do NanoGPT, e cada uma pode faltar sem
 * derrubar as outras — `null` é "não consegui ler", nunca zero:
 *
 * - `assinatura`: os tokens de ENTRADA da semana (o NanoGPT conta assim:
 *   "Input-token quotas count tokens, not operation counts or dollars"), com o
 *   reinício. `degradada` é o `degraded: true` dele, que manda mostrar "não sei",
 *   e não a cota cheia;
 * - `saldoUsd`: o saldo de crédito (`check-balance`);
 * - `gastoSemanaUsd`: o gasto REAL da chave nos últimos 7 dias UTC
 *   (`/api/v1/usage`, `netCostUsd`) — chamada coberta pela assinatura entra
 *   com zero, que é o que ela custou (medido em 26/10).
 */
export const ContaNanogptSchema = z.object({
  assinatura: z
    .object({
      ativa: z.boolean(),
      usadosSemana: z.number().int().nonnegative().nullable(),
      limiteSemana: z.number().int().positive().nullable(),
      reiniciaEm: z.string().datetime().nullable(),
      degradada: z.boolean(),
    })
    .nullable(),
  saldoUsd: z.number().nullable(),
  gastoSemanaUsd: z.number().nullable(),
  lidoEm: z.string().datetime(),
});
export type ContaNanogpt = z.infer<typeof ContaNanogptSchema>;

/** Query do `GET /opencode/modelos`: a busca no catálogo inteiro (D12). */
export const BuscarModelosOpencodeSchema = z.object({
  busca: z.string().trim().max(100).optional(),
});
export type BuscarModelosOpencode = z.infer<typeof BuscarModelosOpencodeSchema>;

/**
 * Resposta do `GET /opencode/modelos`: favoritos primeiro, depois o catálogo
 * (só modelos com `tools`). O teto existe porque o catálogo do OpenRouter tem
 * centenas de modelos e a busca vazia não deve despejar todos na tela.
 */
export const CatalogoOpencodeSchema = z.object({
  modelos: z.array(ModeloOpencodeSchema).max(500),
  /** Quando o catálogo foi lido do OpenRouter (cache de 1 h no servidor). */
  lidoEm: z.iso.datetime(),
  /**
   * O modelo e o esforço que a tela de CRIAR vem marcando, e o modelo que lê
   * as imagens — os da config da frota (D13/D16/D23 revista).
   *
   * Existe porque quem cria sessão é OPERADOR, e OPERADOR não lê
   * `GET /configuracoes/opencode` (ADMIN): sem isto a tela de criar não saberia
   * o padrão escolhido pelo dono. Só estes três campos, e não a config inteira
   * — o resto (favoritos já vêm na ordem, modelo da mestre) não é assunto de
   * quem cria sessão comum.
   *
   * Opcional e SEM `.default()`: uma API anterior não o manda, e um default
   * aqui faria o painel afirmar como "padrão do dono" o que é só a constante.
   */
  padrao: z
    .object({
      modelo: IdModeloOpencodeSchema,
      esforco: EsforcoOpencodeSchema,
      modeloDeVisao: IdModeloOpencodeSchema,
    })
    .optional(),
});
export type CatalogoOpencode = z.infer<typeof CatalogoOpencodeSchema>;

/**
 * Converte o catálogo do OpenCode na forma que a tela de criar já desenha
 * (`ModeloCombinado`). É o despacho de `combinarModelos` para o OPENCODE.
 *
 * O catálogo aqui É a política, como no Codex: a lista vem de fora (OpenRouter,
 * pela API) e nada da casa se soma a ela. `exigeConfirmacao` é `false` porque a
 * confirmação do OpenCode é POR MOTOR (`confirmacaoOpencode`), não por modelo —
 * e confundir as duas faria a guarda do Fable perguntar sobre o DeepSeek.
 */
export function combinarModelosOpencode(catalogo: readonly ModeloDaMaquina[]): ModeloCombinado[] {
  return catalogo.map((m) => ({
    id: m.id,
    // Sem rótulo, o id cru: id feio é melhor que nome inventado.
    rotulo: m.rotulo ?? m.id,
    exigeConfirmacao: false,
    esforcos: m.esforcosDoMotor ?? [],
    padrao: m.padrao === true,
    // Sem `esforcoPadrao` do catálogo, a escolha explícita de não forçar.
    esforcoPadrao: m.esforcoPadrao ?? ESFORCO_PADRAO_OPENCODE,
  }));
}

/**
 * O modelo que a tela vem marcando no OPENCODE: o que o catálogo marcou como
 * padrão (a API marca o `modeloPadrao` da config) e, sem marca, o da D13.
 *
 * Diferente do Codex, aqui existe política da casa — a D13 é decisão do dono —,
 * então cair nela não é inventar nome de modelo.
 */
export function modeloPadraoOpencode(catalogo: readonly ModeloDaMaquina[]): string {
  return catalogo.find((m) => m.padrao === true)?.id ?? MODELO_PADRAO_OPENCODE;
}
